import { useMemo } from "react";
import HeroCarousel from "./components/HeroCarousel";
import CategoryEntries from "./components/CategoryEntries";
import MovieSection from "./components/MovieSection";
import HotRanking from "./components/HotRanking";
import { movies } from "@/mocks/movies";
import { Link } from "react-router-dom";

export default function Home() {
  const latest = useMemo(
    () => [...movies].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1)).slice(0, 10),
    [],
  );

  const hot = useMemo(() => [...movies].sort((a, b) => b.views - a.views), []);
  const hotTop = hot.slice(0, 6);
  const hotGrid = hot.slice(5, 10);

  return (
    <div className="w-full">
      <h1 className="sr-only">91在线视频 - 高清影视视频在线播放平台</h1>
      <HeroCarousel />

      <div className="mx-auto w-full max-w-screen-2xl px-4 md:px-8">
        <section className="mt-12">
          <div className="mb-5 flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500/15 text-accent-500">
              <i className="ri-apps-2-line text-xl" />
            </span>
            <div>
              <h2 className="font-heading text-xl font-600 text-foreground-950 md:text-2xl">
                分类快捷入口
              </h2>
              <p className="mt-0.5 text-xs text-foreground-500">快速进入你感兴趣的影视频道</p>
            </div>
          </div>
          <CategoryEntries />
        </section>

        <MovieSection
          title="最新影视"
          subtitle="按更新时间排序，第一时间掌握最新资源"
          icon="ri-time-line"
          movies={latest}
          moreLink="/category/movie"
        />

        <section className="mt-12">
          <div className="mb-5 flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500/15 text-accent-500">
              <i className="ri-fire-line text-xl" />
            </span>
            <div>
              <h2 className="font-heading text-xl font-600 text-foreground-950 md:text-2xl">
                热门影视推荐
              </h2>
              <p className="mt-0.5 text-xs text-foreground-500">根据播放热度虚拟推荐，人气之选</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[360px_1fr]">
            <div className="rounded-lg border border-background-200/70 bg-background-100/60 p-4">
              <div className="mb-3 flex items-center gap-2">
                <i className="ri-trophy-line text-accent-500" />
                <h3 className="font-heading text-base font-600 text-foreground-950">热播榜</h3>
              </div>
              <HotRanking movies={hotTop} />
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
              {hotGrid.map((movie) => (
                <Link
                  key={movie.id}
                  to={`/detail/${movie.id}`}
                  className="group relative overflow-hidden rounded-lg border border-background-200/70 bg-background-100 transition-colors duration-300 hover:border-accent-500/60"
                >
                  <div className="relative h-[240px] w-full overflow-hidden">
                    <img
                      src={movie.backdrop}
                      alt={`${movie.title}剧照`}
                      title={`${movie.title} - 91在线视频`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/30 to-transparent" />
                  </div>
                  <div className="absolute inset-x-0 bottom-0 p-3">
                    <h3 className="line-clamp-1 text-sm font-600 text-foreground-950 transition-colors group-hover:text-accent-500">
                      {movie.title}
                    </h3>
                    <div className="mt-1 flex items-center justify-between text-xs text-foreground-500">
                      <span>{movie.year}</span>
                      <span className="flex items-center gap-1 text-accent-500">
                        <i className="ri-fire-line" />
                        {movie.views}万
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="mt-14 rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-10">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-[1.4fr_1fr]">
            <div>
              <h2 className="font-heading text-2xl font-600 text-foreground-950">
                关于 91在线视频
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                91在线视频是一家聚焦高清影视内容在线播放的平台，汇聚院线电影、最新电视剧以及热门综艺节目。
                我们坚持清晰分类、便捷筛选与丰富推荐，帮助每一位影视爱好者快速找到心动的作品，
                享受舒适流畅的浏览与观影体验。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/category/movie"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-600 text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-compass-3-line" />
                  浏览全部影视
                </Link>
                <Link
                  to="/about"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/60 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-500"
                >
                  了解更多
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {[
                { num: "16+", label: "精选影视" },
                { num: "3", label: "影视频道" },
                { num: "高清", label: "画质体验" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="flex flex-col items-center justify-center rounded-lg border border-background-200/70 bg-background-50 p-4 text-center"
                >
                  <span className="font-heading text-2xl font-700 text-accent-500">{stat.num}</span>
                  <span className="mt-1 text-xs text-foreground-500">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}